import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, Lock, ArrowRight, RefreshCw } from 'lucide-react';
import { authService } from '../../services/authService';
import { authError } from '../../utils/authError';
import { persistSessionNative } from '../../utils/nativeSessionStore';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [suspended, setSuspended] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  // One-time notice if we were bounced here by the session guard (token expired
  // or the account was signed in on another device).
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const reason = localStorage.getItem('sessionEndedReason');
    if (reason) {
      setNotice(reason === 'another-device'
        ? 'You were signed out because your account was used on another device.'
        : 'Your session expired. Please sign in again.');
      localStorage.removeItem('sessionEndedReason');
    }
  }, []);

  // Show the suspended screen if the protected-route status check (cached
  // session) flagged this account as suspended.
  useEffect(() => {
    if (localStorage.getItem('suspended') === '1') {
      setSuspended(true);
      setSuspendReason(localStorage.getItem('suspendedReason') || '');
    }
  }, []);

  useEffect(() => {
    if ('OTPCredential' in window && step === 'otp') {
      const ac = new AbortController();
      (navigator as any).credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      }).then((otp: any) => {
        if (otp && otp.code) {
          setOtp(otp.code);
          setTimeout(() => {
            if (otp.code.length === 6) handleVerifyOTP();
          }, 500);
        }
      }).catch(() => {});
      return () => ac.abort();
    }
  }, [step]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleSendOTP = async () => {
    if (phone.length < 9) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await authService.requestOTP(phone);
      setSessionId(data.sessionId);
      setStep('otp');
      setCountdown(30);
    } catch (err: any) {
      setError(authError(err, 'Could not send the code. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 6 && step === 'otp') {
      handleVerifyOTP();
    }
  }, [otp]);

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await authService.verifyOTP(phone, otp, sessionId);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      if (data.supabaseToken) localStorage.setItem('supabaseToken', data.supabaseToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('loginAt', String(Date.now()));
      localStorage.removeItem('sessionEndedReason');
      persistSessionNative();
      navigate('/home');
    } catch (err: any) {
      if (err.response?.data?.error === 'suspended') {
        setSuspendReason(err.response?.data?.message || err.response?.data?.reason || '');
        setSuspended(true);
      } else {
        setError(authError(err, 'Invalid verification code.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setOtp('');
    setError('');
    try {
      const data = await authService.requestOTP(phone);
      setSessionId(data.sessionId);
      setCountdown(30);
    } catch (err: any) {
      setError(authError(err, 'Could not resend the code.'));
    }
  };

  if (suspended) {
    return (
      <div className="fixed inset-0 overflow-hidden bg-safaricab-darker flex flex-col items-center justify-center px-6 text-center">
        <img src="/icons/suspended.png" alt="Account suspended" className="w-48 h-auto mb-6" />
        <h2 className="text-white font-bold text-2xl mb-2">Account Suspended</h2>
        {suspendReason && (
          <div className="bg-safaricab-card border border-safaricab-gold/30 rounded-xl px-4 py-3 mb-4 max-w-sm">
            <p className="text-safaricab-gold text-[11px] font-semibold uppercase tracking-wide mb-1">Reason</p>
            <p className="text-white/80 text-sm leading-relaxed">{suspendReason}</p>
          </div>
        )}
        <p className="text-white/50 text-sm mb-8">Please contact customer care for assistance.</p>
        <a href="tel:+254700000000"
          className="w-full max-w-sm bg-safaricab-gold text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 mb-4">
          <Phone className="w-5 h-5" /> Contact Customer Care
        </a>
        <button onClick={() => { localStorage.removeItem('suspended'); localStorage.removeItem('suspendedReason'); setSuspended(false); }} className="text-white/30 text-sm hover:text-white/60">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-safaricab-darker flex flex-col touch-none">
      <div className="px-4 pt-[calc(max(var(--sai-top),14px)+24px)]">
        <button onClick={() => step === 'otp' ? setStep('phone') : navigate('/')} className="text-white/50 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">{step === 'otp' ? 'Change number' : 'Back'}</span>
        </button>
      </div>
      <div className="px-4 pt-6 mb-6 flex flex-col items-center text-center">
        <img src="/logo-lion.png" alt="SafariCab" className="w-28 h-28 object-contain mb-2" />
        <h3 className="text-white font-bold text-base tracking-wider">SAFARICAB</h3>
        <p className="text-safaricab-gold text-[10px] tracking-widest">ONLINE TAXI SERVICE</p>
      </div>
      <div className="px-4 flex-1 flex flex-col justify-center" style={{ paddingBottom: 'calc(var(--sai-bottom) + 6rem)' }}>
        {notice && (
          <div className="mb-4 text-amber-300 text-sm text-center bg-amber-400/10 border border-amber-400/20 py-3 px-3 rounded-xl">
            {notice}
          </div>
        )}
        <AnimatePresence mode="wait">
          {step === 'phone' ? (
            <motion.div key="phone" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-white mb-1">Welcome Back</h1>
                <p className="text-white/50 text-sm">Sign in to continue riding</p>
              </div>
              <div className="space-y-2">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value.replace(/[^0-9+]/g, '')); setError(''); }} placeholder="+254712345678" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white text-lg placeholder-white/20 outline-none focus:border-safaricab-gold/50" />
                </div>
              </div>
              {error && <p className="text-red-400 text-sm text-center bg-red-400/5 py-3 rounded-xl">{error}</p>}
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleSendOTP} disabled={isLoading || phone.length < 9} className="w-full bg-safaricab-gold hover:bg-yellow-500 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-3 disabled:opacity-40">
                {isLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Lock className="w-5 h-5" /><span>Send Verification Code</span></>}
              </motion.button>
            </motion.div>
          ) : (
            <motion.div key="otp" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-white mb-1">Verify Code</h1>
                <p className="text-white/50 text-sm">Enter the code sent to <span className="text-safaricab-gold">{phone}</span></p>
                <p className="text-white/30 text-xs mt-1">Check your SMS or email for the code</p>
              </div>
              <div className="space-y-2">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Verification Code</label>
                <input type="text" inputMode="numeric" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} placeholder="000000" maxLength={6} className="w-full bg-safaricab-card border border-white/10 rounded-xl py-4 px-4 text-white text-center text-2xl tracking-[0.7em] placeholder-white/10 outline-none focus:border-safaricab-gold/50 font-mono" />
              </div>
              {error && <p className="text-red-400 text-sm text-center bg-red-400/5 py-3 rounded-xl">{error}</p>}
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleVerifyOTP} disabled={isLoading || otp.length !== 6} className="w-full bg-safaricab-gold hover:bg-yellow-500 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-3 disabled:opacity-40">
                {isLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><span>Verify & Continue</span><ArrowRight className="w-5 h-5" /></>}
              </motion.button>
              <button onClick={handleResendOTP} disabled={countdown > 0} className="w-full text-center text-sm text-white/40 hover:text-safaricab-gold disabled:text-white/20">
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend Code'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Login;
